import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  GridItem,
  Heading,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { getCustomerBalancesAcrossPartners } from 'api/internaltools';
import { useAuthentication } from 'AuthContext';
import { PermissionsV2 } from 'constants/permissions_v2';
import AlertMessage from 'components/AlertMessage/AlertMessage';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import Loader from 'components/Loader/Loader';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);

  if (Number.isNaN(num)) return '—';

  return num.toLocaleString(undefined, { maximumFractionDigits: 8 });
};

const partnerColor = (code) => {
  const c = (code || '').toUpperCase();

  if (c.includes('ONE') || c.includes('MONEY')) return 'purple';
  if (c.includes('LAYER')) return 'blue';

  return 'gray';
};

export default function MerchantPartnerBalancesCard({ merchantId }) {
  const { userPermissions } = useAuthentication();
  const canView = userPermissions?.has(PermissionsV2.VIEW_PARTNER_VISIBILITY);

  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!merchantId || !canView) return;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getCustomerBalancesAcrossPartners(merchantId);

        setPayload(response?.data?.data ?? null);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [merchantId, canView]);

  if (!canView) {
    return (
      <GridItem w='100%'>
        <Heading as='h2' size='md' mb={3}>
          Partner balances
        </Heading>
        <Card minH='300px' maxH='400px' overflow='auto' style={{ borderWidth: '2px' }}>
          <CardBody>
            <Text color='gray.600' fontSize='sm'>
              You need <strong>Partner visibility</strong> permission to view partner-side balances for this merchant.
            </Text>
          </CardBody>
        </Card>
      </GridItem>
    );
  }

  const partners = payload?.partners ?? [];
  const hasAnyCustomer = partners.some((p) => (p?.customers ?? []).length > 0);

  return (
    <GridItem w='100%' style={{ display: 'flex', flexDirection: 'column' }}>
      <Heading as='h2' size='md' mb={3}>
        Partner balances
      </Heading>
      <Card minH='300px' maxH='480px' overflow='auto' style={{ borderWidth: '2px' }}>
        <CardBody>
          {isLoading && <Loader />}
          {error && (
            <AlertMessage
              errorTitle='Could not load partner balances'
              errorMessage={error?.response?.data?.message || error?.message || 'Request failed'}
            />
          )}

          {!isLoading && !error && !hasAnyCustomer && (
            <Text color='gray.600' fontSize='sm'>
              No partner customer balances were returned for this merchant (e.g. no OneMoney payout accounts mapped).
            </Text>
          )}

          {!isLoading && !error && hasAnyCustomer && (
            <Stack spacing={5} divider={<Divider />}>
              {partners.map((partner) => {
                const customers = partner?.customers ?? [];

                if (!customers.length) return null;

                return (
                  <Box key={partner.partner_code || 'unknown'}>
                    <Badge colorScheme={partnerColor(partner.partner_code)} fontSize='0.75em' mb={3}>
                      {partner.partner_code}
                    </Badge>

                    <Stack spacing={4}>
                      {customers.map((cust) => (
                        <Box
                          key={cust.customer_id}
                          borderWidth='1px'
                          borderColor='gray.200'
                          borderRadius='md'
                          overflow='hidden'
                          bg='gray.50'
                        >
                          <Box px={3} py={2} bg='white' borderBottomWidth='1px' borderColor='gray.200'>
                            <Text fontSize='xs' color='gray.500'>
                              Partner customer ID
                            </Text>
                            <Text fontSize='sm' fontWeight='semibold' fontFamily='mono'>
                              {cust.customer_id}
                            </Text>
                            {cust.customer_name ? (
                              <Text fontSize='xs' color='gray.600' mt={1}>
                                {cust.customer_name}
                              </Text>
                            ) : null}
                          </Box>

                          <TableContainer>
                            <Table size='sm' variant='simple'>
                              <Thead bg='gray.100'>
                                <Tr>
                                  <Th>Asset</Th>
                                  <Th isNumeric>Available</Th>
                                  <Th isNumeric>Hold</Th>
                                  <Th isNumeric>Total</Th>
                                </Tr>
                              </Thead>
                              <Tbody bg='white'>
                                {(cust.balances ?? []).map((b) => (
                                  <Tr key={`${cust.customer_id}-${b.currency_code}`}>
                                    <Td fontWeight='medium'>{b.currency_code}</Td>
                                    <Td isNumeric>{formatNumber(b.available_balance)}</Td>
                                    <Td isNumeric>{formatNumber(b.hold_balance)}</Td>
                                    <Td isNumeric fontWeight='semibold'>
                                      {formatNumber(b.total_balance)}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardBody>
      </Card>
    </GridItem>
  );
}
